import {
  Avatar,
  ButtonList,
  DropdownButton,
  FlexBox,
  Spinner,
  Table,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import {
  FullPresenceT,
  LoadDescriptionT,
  UserPresence,
} from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import * as React from 'react';
import { ReactNode } from 'react';

import { WorkloadLimits } from 'apis/presence';

import styles from '../../teamPage.module.scss';
import { LoadingDropdownItem } from '../loadingDropdownItem';
import { TeamCard } from '../teamCard';
import { TeamChannelIconsList } from '../teamChannelIconsList';
import { TeamConversation } from '../teamConversation/TeamConversation';
import type { TeamListProps } from '../teamList/teamList';

type PropsT = {
  userPresence: UserPresence;
  isOwnAgent: boolean;
  profileId: string;
  isFavorite: boolean;
  isOpen: boolean;
  limits: WorkloadLimits;
  onFavoriteClick: () => void;
  toggleAgentDetails: (id: string) => void;
} & Pick<
  TeamListProps,
  | 'appPresence'
  | 'showUserModal'
  | 'isMicrophoneBlocked'
  | 'twilioMakeInternalCall'
  | 'toggleAgentDetails'
  | 'transferToQueueModalOpen'
  | 'getPresence'
>;

export class TeamPageTableRow extends React.PureComponent<PropsT> {
  @boundMethod
  findFullPresence(): FullPresenceT | undefined {
    const { appPresence, userPresence } = this.props;
    const tail = appPresence.items[appPresence.items.length - 1];
    // To prevent iterations, first check last item.
    // It is getting added with Array.prototype.push
    if (tail?.user.id === userPresence.agent_id) {
      return tail;
    }

    return appPresence.items.find((x) => x.user.id === userPresence.agent_id);
  }

  @boundMethod
  loadDescriptions(): LoadDescriptionT[] {
    const fullPresence = this.findFullPresence();
    return (
      fullPresence?.presence.presence_status.load_descriptions.filter(
        (ld) => ld.conversation,
      ) || []
    );
  }

  @boundMethod
  getPresenceStatus(): string {
    const { userPresence } = this.props;

    // If connection status is offline user is considered to be also presence `offline`.
    return userPresence.connection_status === 'offline'
      ? 'offline'
      : userPresence.status;
  }

  @boundMethod
  getChannelCapabilities(): {
    isSimultaneousRingingActive: boolean;
    canMakeInternalCall: boolean;
  } {
    const { userPresence, isMicrophoneBlocked, isOwnAgent } = this.props;
    const presenceStatus = this.getPresenceStatus();

    // WORKAROUND: We sometimes receive WS events having some load_descriptions with undefined conversation.
    // The following is just a workaround, but the real fix lies in the presence service. Until then, we need
    // to stick with this.
    //
    // See: https://dixa-product.atlassian.net/browse/DIXA-2895
    const { non_working_channels } = userPresence;

    const foundSpeakLoad = this.loadDescriptions().filter((load) => {
      const { conversation_channel: channel, load_type: loadType } = load;
      return (
        ['pstnphone', 'callback'].includes(channel) &&
        !['acw', 'nonload'].includes(loadType)
      );
    }).length;

    const canMakeInternalCall = !(
      foundSpeakLoad > 0 ||
      isMicrophoneBlocked ||
      isOwnAgent ||
      presenceStatus === 'offline' ||
      non_working_channels.some((channel) => channel === 'pstnphone')
    );

    return {
      isSimultaneousRingingActive: userPresence.simultaneous_ringing,
      canMakeInternalCall,
    };
  }

  @boundMethod
  teamToggleAgentCardClickHandler(isOpen: boolean): void {
    const { userPresence, isOwnAgent, toggleAgentDetails } = this.props;
    const fullPresence = this.findFullPresence();
    if (!fullPresence) {
      this.props.getPresence({
        isOwnAgent,
        user_id: userPresence.agent_id,
      });
    }

    isOpen ? ampli.teamAgentExpanded() : ampli.teamAgentCollapsed();

    toggleAgentDetails(userPresence.agent_id);
  }

  @boundMethod
  twilioMakeInternalCall(): void {
    const { userPresence, twilioMakeInternalCall } = this.props;
    twilioMakeInternalCall({ userId: userPresence.agent_id });
  }

  @boundMethod
  showUserModal(): void {
    const { showUserModal } = this.props;
    const fullPresence = this.findFullPresence();
    if (fullPresence?.user) {
      showUserModal(fullPresence.user);
    } else {
      console.warn('No user in presence defined');
    }
  }

  @boundMethod
  onActionsDropdownClick(e: React.MouseEvent): void {
    e.stopPropagation(); // Prevent card below from opening on click

    const { userPresence, isOwnAgent } = this.props;
    const fullPresence = this.findFullPresence();

    if (!fullPresence) {
      this.props.getPresence({
        isOwnAgent,
        user_id: userPresence.agent_id,
      });
    }
  }

  @boundMethod
  onEditClick(): void {
    this.showUserModal();
    ampli.teamAgentActionsMenuItemClicked({ type: 'edit' });
  }

  @boundMethod
  onCallClick(): void {
    this.twilioMakeInternalCall();
    ampli.teamAgentActionsMenuItemClicked({ type: 'call' });
  }

  @boundMethod
  getAgentName(presence?: FullPresenceT): ReactNode {
    const { userPresence, isOwnAgent } = this.props;
    const displayName = (userPresence.name || '').trim();
    const agentName = `${displayName} ${isOwnAgent ? '(you)' : ''}`;
    return agentName;
  }

  @boundMethod
  getAvatarWithFullNameTooltip(presence?: FullPresenceT): ReactNode {
    const { userPresence, isOwnAgent } = this.props;
    const displayName = (userPresence.name || '').trim();
    const agentName = `${displayName} ${isOwnAgent ? '(you)' : ''}`;
    const { user: { given_name = '', family_name = '' } = {} } = presence || {};
    const fullName = `${given_name || ''} ${family_name || ''}`.trim();
    const name = fullName || displayName;

    return (
      <Tooltip dark content={name}>
        <Avatar
          user={{
            id: userPresence.agent_id,
            name: agentName,
            avatar_url: userPresence.avatar_url,
          }}
        />
      </Tooltip>
    );
  }

  render() {
    const {
      userPresence,
      appPresence,
      limits,
      isFavorite,
      isOpen,
      onFavoriteClick,
      transferToQueueModalOpen,
      profileId,
    } = this.props;

    const { workloads_by_channel } = userPresence;

    const fullPresence = this.findFullPresence();
    const presenceStatus = this.getPresenceStatus();
    const capabilities = this.getChannelCapabilities();

    const conversationList = (conversationRequester: LoadDescriptionT) => {
      const { csid, conversation, requester, conversation_channel, load_type } =
        conversationRequester;
      return (
        <TeamConversation
          key={csid}
          channel={conversation_channel}
          loadType={load_type}
          csid={csid}
          profileId={profileId}
          conversation={conversation}
          user={fullPresence?.user ?? null}
          requester={requester}
          transferToQueueModalOpen={transferToQueueModalOpen}
        />
      );
    };

    // TODO (TEAMS-3462): replace team card with habla card component.
    // NOTE: This should also fix TEAMS-3468 (see further down).
    return (
      <Table.Tr>
        <Table.Td style={{ padding: 0 }}>
          <TeamCard isOpen={isOpen}>
            <TeamCard.Header
              iconsList={
                <TeamCard.Icons>
                  <TeamChannelIconsList
                    workloads_by_channel={workloads_by_channel}
                    fullPresence={fullPresence}
                    limits={limits}
                    isSimultaneousRingingActive={
                      capabilities.isSimultaneousRingingActive
                    }
                  />
                </TeamCard.Icons>
              }
              actions={
                // @TODO https://dixa-product.atlassian.net/browse/TEAMS-3468
                <div onClick={(e) => this.onActionsDropdownClick(e)}>
                  <DropdownButton
                    icon="dots-h"
                    noArrow
                    buttonProps={{ variant: 'tertiary' }}
                    onToggle={(isOpen) =>
                      isOpen && ampli.teamAgentActionsMenuOpened()
                    }
                    dropdown={
                      appPresence.asyncStatus === 'loading' ? (
                        <LoadingDropdownItem />
                      ) : (
                        <ButtonList className={styles.actionsList}>
                          {capabilities.canMakeInternalCall && (
                            <ButtonList.Item
                              onClick={this.onCallClick}
                              icon="phone"
                            >
                              Call
                            </ButtonList.Item>
                          )}
                          <ButtonList.Item
                            onClick={this.onEditClick}
                            icon="pen"
                          >
                            Edit
                          </ButtonList.Item>
                        </ButtonList>
                      )
                    }
                  />
                </div>
              }
              status={presenceStatus}
              avatar={this.getAvatarWithFullNameTooltip(fullPresence)}
              isFavorite={isFavorite}
              onFavoriteClick={onFavoriteClick}
              isOpen={isOpen}
              onToggle={this.teamToggleAgentCardClickHandler}
            >
              {this.getAgentName(fullPresence)}
            </TeamCard.Header>
            <TeamCard.Content>
              {this.loadDescriptions().length === 0 && (
                <FlexBox justifyContent="center" alignItems="center">
                  {appPresence.asyncStatus === 'loading' && !fullPresence ? (
                    <Spinner />
                  ) : (
                    'No conversations found'
                  )}
                </FlexBox>
              )}
              {isOpen && this.loadDescriptions().map(conversationList)}
            </TeamCard.Content>
          </TeamCard>
        </Table.Td>
      </Table.Tr>
    );
  }
}
